import React, { useEffect, useMemo, useRef, useState } from 'react'

import Button from '@react/react-spectrum/Button'
import ListDataSource from '@react/react-spectrum/ListDataSource'
import Select from '@react/react-spectrum/Select'
import { TableView } from '@react/react-spectrum/TableView'

import Add from '@react/react-spectrum/Icon/Add'
import Delete from '@react/react-spectrum/Icon/Delete'
import PageExclude from '@react/react-spectrum/Icon/PageExclude'

import { useScreens } from 'hooks'

import '../styles/ScreenPicker.scss'

let columns = [
  {
    title: 'Screens',
    key: 'name',
    width: 192,
  },
  {
    title: '',
    key: 'actions',
    width: 30,
  },
]

const EmptyState = () => (
  <div className='empty-state-wrapper'>
    <PageExclude size={'L'} />
    <p>No screens selected.</p>
  </div>
)

class ScreensDataSource extends ListDataSource {
  async setScreens(screens) {
    this.screens = screens
    return await this.load()
  }
  async load(sortDescriptor) {
    return Array.isArray(this.screens) && this.screens.map((s, idx) => ({ name: s, idx }))
  }
  performLoad(...args) {
    super.performLoad(...args)
  }
}

const MultipleScreenPicker = ({ screens, screenName, filter, disabled, onChange }) => {
  const { screens: projectScreens } = useScreens()
  const [screenList, setScreenList] = useState(screens || [])
  const ds = useRef(new ScreensDataSource())
  const screensDataSource = ds.current

  const screenOptions = useMemo(
    () =>
      projectScreens
        .filter(s => s.meta.name !== screenName)
        .filter(filter)
        .map(s => ({ label: s.meta.title || s.meta.name, value: s.meta.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [projectScreens, screenName, filter]
  )

  const saveScreens = list => {
    // Save only existing screens
    const filteredScreenList = list.filter(s => !!s)

    onChange(filteredScreenList)
  }

  const updateScreens = data => name => {
    let list = [...screenList]
    list[data.idx] = name
    setScreenList(list)
    saveScreens(list)
  }

  const addScreen = () => {
    setScreenList(list => list.concat(''))
  }

  const removeScreen = idx => {
    const list = [...screenList]
    list.splice(idx, 1)
    setScreenList(list)
    saveScreens(list)
  }

  const renderCell = (column, data) => {
    switch (column.key) {
      case 'name':
        return <Select options={screenOptions} value={data.name} onSelect={updateScreens(data)} disabled={disabled} />
      case 'actions':
        return <Button variant='tool' icon={<Delete />} onClick={() => removeScreen(data.idx)} disabled={disabled} />
      default:
        return <span>{data[column.key]}</span>
    }
  }

  useEffect(() => {
    setScreenList(screens || [])
  }, [screens])

  useEffect(() => {
    screensDataSource.setScreens(screenList).then(() => screensDataSource.reloadData())
  }, [screenList, screensDataSource])

  return (
    <div className='screen-picker'>
      <TableView
        className='table-view'
        columns={columns}
        dataSource={screensDataSource}
        renderCell={renderCell}
        renderEmptyView={EmptyState}
        acceptDrops={true}
        allowsSelection={false}
        allowsMultipleSelection={false}
        canReorderItems={true}
        dropPosition='between'
      />
      <Button
        variant='tool'
        label='Add screen'
        icon={<Add />}
        className='add-screen'
        onClick={addScreen}
        disabled={screenList.indexOf('') > -1 || disabled}
      />
    </div>
  )
}

MultipleScreenPicker.defaultProps = {
  filter: Boolean,
}

const SingleScreenPicker = ({ value, screenName, filter, disabled, onChange }) => {
  const { screens } = useScreens()
  const screenOptions = useMemo(
    () =>
      screens
        .filter(s => s.meta.name !== screenName)
        .filter(filter)
        .map(s => ({ label: s.meta.title || s.meta.name, value: s.meta.name }))
        .sort((a, b) => a.label.localeCompare(b.label))
        .concat([
          { label: 'Checkout', value: 'purchase' },
          { label: 'Checkout (Optionated)', value: 'optionatedPurchase' },
          { label: '---', value: '#' },
        ]),
    [screens, screenName, filter]
  )

  return <Select options={screenOptions} value={value} onSelect={onChange} disabled={disabled} />
}

SingleScreenPicker.defaultProps = {
  filter: Boolean,
}

export const ScreenPicker = ({ multiple, ...props }) => {
  return multiple ? <MultipleScreenPicker {...props} /> : <SingleScreenPicker {...props} />
}
